import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { colors, theme } from 'src/theme';
import fileDownload from 'js-file-download';
import { Checkbox, Table, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Loader } from 'src/components/shared/loader/loader';
import { TextBox } from 'src/components/shared/textbox/textbox';
import { CustomDialog } from 'src/components/shared/dialog/dialog';
import {
	RoutineDetailsDataType,
	RoutineDetailsType,
	RoutineDocumentType,
	UploadRoutineDocRequest,
} from 'src/types/routine.types';
import { deleteFile, downloadFile, getBlobSasToken, uploadFile } from 'src/services/verification-routine-service';
import {
	deleteFile as deleteMatFile,
	downloadFile as downloadMatFile,
	getBlobSasToken as getMatBlobSasToken,
	uploadFile as uploadMatFile,
} from 'src/services/material-routine-service';
import { msalInstance } from 'src/config/msalConfig';
import { useAsyncOperation } from 'src/utils/use-async-operation';
import { PostResponse } from 'src/types/common.types';
import { CustomAlert } from 'src/components/shared/alert/alert';
import { RoutineDocTable } from './routineDocTable';

const testid = 'routine-details';

const color = colors();
const themes = theme().typography;
export const RoutineDetailsDialog = ({
	isAdmin,
	isMaterial,
	isError,
	data,
	onSaveClick,
	onCancelClick,
}: RoutineDetailsType): ReactElement => {
	const classes = useStyles();
	const userAccount = msalInstance.getActiveAccount();
	const loggedInUser = userAccount ? `${userAccount.name?.split('(')[0]}| ${userAccount.username}` : '';
	const maxRoutineDocumentsMessage = 'A maximum of four routine documents are allowed.';

	const inputRef = useRef<HTMLInputElement>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [loaderMessage, setLoaderMessage] = useState<string>('Saving in progress..');
	const [openError, setOpenError] = useState<boolean>(false);
	const [checked, setChecked] = useState<boolean>(data.allowMaterialEdits);
	const [errorMessage, setErrorMessage] = useState<string>('Something went wrong. Please try again later.');
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
	const [description, setDescription] = useState<string>(data.routineDescription || '');
	const [matDescription, setMatDescription] = useState<string>(data.materialDescription || '');
	const [compositionRequirement, setCompositionRequirement] = useState<string>(data.compositionRequirement || '');
	const [blendingRestriction, setBlendingRestriction] = useState<string>(data.blendingRestriction || '');
	const [sampleCertification, setSampleCertification] = useState<string>(data.sampleCertification || '');
	const [certificationApplication, setCertificationApplication] = useState<string>(data.certificationApplication || '');
	const [documents, setDocuments] = useState<RoutineDocumentType[]>(data.uploadedDocuments || []);
	const [docName, setDocName] = useState<string>();
	const [docType, setDocType] = useState<string>('Required');

	const [removeFile, deleteBusy, deleteData, deleteError] = useAsyncOperation<PostResponse>(
		isMaterial ? deleteMatFile : deleteFile
	);
	const [getSasToken, tokenBusy, sasToken, tokenError] = useAsyncOperation<string>(
		isMaterial ? getMatBlobSasToken : getBlobSasToken
	);

	useEffect(() => {
		setLoaderMessage('Updating...');
		setLoading(true);
	}, [deleteBusy]);

	useEffect(() => {
		setLoading(false);
		if (deleteError) {
			setErrorMessage('Something went wrong while deleting the document.');
			setOpenError(true);
		}
	}, [deleteError]);

	useEffect(() => {
		if (deleteData) {
			setLoading(false);
			let docs = documents.filter((doc) => doc.documentName !== docName);
			setDocuments(docs);
		}
	}, [deleteData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setLoaderMessage('Opening document...');
		setLoading(true);
	}, [tokenBusy]);

	useEffect(() => {
		setLoading(false);
		if (tokenError) {
			setErrorMessage('Something went wrong while opening the document.');
			setOpenError(true);
		}
	}, [tokenError]);

	useEffect(() => {
		if (sasToken) {
			setLoading(false);
			let url = documents.filter((d) => d.documentName === docName)[0].documentUrl;
			window.open(`${url}${sasToken}`);
		}
	}, [sasToken]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleFileClick = () => {
		let doc = isMaterial
			? documents.filter((d) => d.documentType.toLowerCase() === 'material')
			: documents.filter((d) => d.documentType.toLowerCase() !== 'material');
		if (doc.length === 4) {
			setErrorMessage(maxRoutineDocumentsMessage);
			setOpenError(true);
		} else inputRef.current && inputRef.current.click();
	};

	const handleFileChange = async (event: any) => {
		setLoaderMessage('Uploading document...');
		setLoading(true);
		const file = event.target.files && event.target.files[0];
		if (!file) {
			return;
		}

		let fileUpload: UploadRoutineDocRequest = {
			routineId: data.id,
			documentName: file.name,
		};

		let formData = new FormData();
		formData.append('request', JSON.stringify(fileUpload));
		formData.append('file', file);

		let upload = isMaterial ? uploadMatFile : uploadFile;
		upload(formData)
			.then((res) => {
				setLoading(false);
				setDocuments([
					...documents,
					{
						documentName: res.data.split('||')[0],
						documentType: docType,
						documentUrl: res.data.split('||')[1],
						documentUploadedBy: loggedInUser,
						documentUploadedDate: new Date().toISOString(),
						isNew: true,
					},
				]);
			})
			.catch(() => {
				setLoading(false);
				setErrorMessage('Something went wrong while uploading the document.');
				setOpenError(true);
			});
	};

	const handleDownload = (row: RoutineDocumentType) => {
		setLoaderMessage('Downloading document...');
		setLoading(true);
		let download = isMaterial ? downloadMatFile : downloadFile;
		download(row.documentUrl, row.documentName)
			.then((res) => {
				fileDownload(res.data, row.documentName);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				setErrorMessage('Something went wrong while downloading the document.');
				setOpenError(true);
			});
	};

	const handleClose = () => {
		let newDocs = documents.filter((doc) => doc.isNew === true);
		newDocs.forEach((doc) => {
			removeFile(doc.documentUrl, doc.documentName);
		});

		documents.forEach((doc) => {
			doc.isNew = false;
			doc.isDelete = false;
		});

		setDocuments(data.uploadedDocuments || []);
		onCancelClick();
	};

	const handleSave = () => {
		let deletedDocs = documents.filter((doc) => doc.isDelete === true);
		deletedDocs.forEach((doc) => {
			removeFile(doc.documentUrl, doc.documentName);
		});
		let saveData: RoutineDetailsDataType = {
			...data,
			routineDescription: !isMaterial ? description : '',
			materialDescription: matDescription,
			compositionRequirement: !isMaterial || data.allowMaterialEdits ? compositionRequirement : '',
			blendingRestriction: !isMaterial || data.allowMaterialEdits ? blendingRestriction : '',
			sampleCertification: !isMaterial || data.allowMaterialEdits ? sampleCertification : '',
			certificationApplication: !isMaterial || data.allowMaterialEdits ? certificationApplication : '',
			uploadedDocuments: documents.filter((doc) => !doc.isDelete),
			allowMaterialEdits: !isMaterial ? checked : false,
		};
		onSaveClick(saveData);
	};

	return (
		<>
			<Loader key="edit-loader" open={loading} message={loaderMessage} />
			<CustomDialog
				key={testid}
				open={true}
				width="1200px"
				title={isMaterial ? data.materialName : data.routineName}
				yesButtonText={isAdmin ? 'Save' : ''}
				noButtonText={isAdmin ? 'Cancel' : 'Close'}
				onYesClickCallback={handleSave}
				onNoClickCallback={handleClose}
				onCloseCallback={handleClose}
				component={
					<div>
						<CustomAlert
							open={isError || openError}
							message={errorMessage}
							severity="error"
							timeout={5000}
							isFade={false}
							onCloseCallback={() => {
								setOpenError(false);
							}}
						/>
						{isMaterial && (
							<>
								<div className={classes.group}>
									<TextBox
										id={0}
										label=""
										value={matDescription}
										width={1000}
										isMultiline
										disabled={!isAdmin}
										noBorder={!isAdmin}
										onChangeCallback={setMatDescription}
									/>
								</div>
								<div className={classes.header}>{data.routineName} - Description</div>
							</>
						)}
						<div className={classes.group}>
							<TextBox
								id={0}
								label=""
								value={description}
								width={1000}
								isMultiline
								disabled={isMaterial || !isAdmin}
								noBorder={isMaterial || !isAdmin}
								onChangeCallback={setDescription}
							/>
						</div>
						{!isMaterial && (
							<div className={classes.flexDisplay}>
								<div className={classes.subHeader}>Allow Material Edits</div>
								<Checkbox
									size="small"
									checked={checked}
									onChange={(event) => setChecked(event.target.checked)}
									disabled={!isAdmin}
								/>
							</div>
						)}
						<div className={classes.header}>Routine Requirements</div>
						<div className={classes.requirements}>
							<div className={classes.group}>
								<div className={classes.subHeader}>Composition Requirements</div>
								<TextBox
									id={0}
									label=""
									value={compositionRequirement}
									width={990}
									isMultiline
									isFixedHeight={false}
									disabled={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									noBorder={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									onChangeCallback={setCompositionRequirement}
								/>
							</div>
							<div className={classes.group}>
								<div className={classes.subHeader}>Blending Requirements</div>
								<TextBox
									id={0}
									label=""
									value={blendingRestriction}
									width={990}
									isMultiline
									isFixedHeight={false}
									disabled={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									noBorder={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									onChangeCallback={setBlendingRestriction}
								/>
							</div>
							<div className={classes.group}>
								<div className={classes.subHeader}>Sample Certification</div>
								<TextBox
									id={0}
									label=""
									value={sampleCertification}
									width={990}
									isMultiline
									isFixedHeight={false}
									disabled={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									noBorder={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									onChangeCallback={setSampleCertification}
								/>
							</div>
							<div className={classes.group}>
								<div className={classes.subHeader}>Certificate Application</div>
								<TextBox
									id={0}
									label=""
									value={certificationApplication}
									width={990}
									isMultiline
									isFixedHeight={false}
									disabled={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									noBorder={(isMaterial && !data.allowMaterialEdits) || !isAdmin}
									onChangeCallback={setCertificationApplication}
								/>
							</div>
						</div>
						<TableContainer className={classes.table} data-testid={`${testid}-body`}>
							<Table stickyHeader>
								<TableHead>
									<TableRow key={`${testid}-header`}>
										<TableCell width="15%">Type</TableCell>
										<TableCell width="35%">Uploaded Documents</TableCell>
										<TableCell width="15%">Uploaded Date</TableCell>
										<TableCell width="15%">Actions</TableCell>
										<TableCell width="20%"></TableCell>
									</TableRow>
								</TableHead>
								<RoutineDocTable
									type="Required"
									documents={documents}
									allowEdit={!isMaterial && (isAdmin || data.allowMaterialEdits)}
									handleDownload={handleDownload}
									setDocName={setDocName}
									getSasToken={getSasToken}
									setDeleteDialog={setDeleteDialog}
									setDocType={setDocType}
									handleFileClick={handleFileClick}
								/>
								<RoutineDocTable
									type="Supporting"
									documents={documents}
									allowEdit={!isMaterial && (isAdmin || data.allowMaterialEdits)}
									handleDownload={handleDownload}
									setDocName={setDocName}
									getSasToken={getSasToken}
									setDeleteDialog={setDeleteDialog}
									setDocType={setDocType}
									handleFileClick={handleFileClick}
								/>
								{isMaterial && (
									<RoutineDocTable
										type="Material"
										documents={documents}
										allowEdit={isMaterial && isAdmin}
										handleDownload={handleDownload}
										setDocName={setDocName}
										getSasToken={getSasToken}
										setDeleteDialog={setDeleteDialog}
										setDocType={setDocType}
										handleFileClick={handleFileClick}
									/>
								)}
							</Table>
							<input
								key={`${testid}-fileInput`}
								style={{
									display: 'none',
								}}
								ref={inputRef}
								type="file"
								onChange={(e) => {
									handleFileChange(e);
									if (inputRef.current) inputRef.current.value = '';
								}}
								data-testid={`${testid}-fileInput`}
							/>
						</TableContainer>
					</div>
				}
			/>
			<CustomDialog
				key={`${testid}-delete-dialog`}
				open={deleteDialog}
				title={'Delete Document'}
				yesButtonText="Yes"
				noButtonText="No"
				onYesClickCallback={() => {
					let data = [...documents];
					data.filter((doc) => doc.documentName === docName)[0].isDelete = true;
					setDocuments(data);
					setDeleteDialog(false);
				}}
				onNoClickCallback={() => setDeleteDialog(false)}
				onCloseCallback={() => setDeleteDialog(false)}
				component={
					<div style={{ ...themes.bodyRegular }}>Are you sure you want to delete this document - {docName}?</div>
				}
			/>
		</>
	);
};

const useStyles = makeStyles(() => ({
	flexDisplay: {
		display: 'flex',
		alignItems: 'center',
		gap: '20px',
		marginTop: '5px',
	},
	header: {
		...themes.bodyBold,
		color: color.neutral[8],
		marginTop: '5px',
	},
	subHeader: {
		...themes.bodySmall,
		margin: '3px 0px',
		fontWeight: 500,
		color: color.neutral[8],
	},
	group: {
		'& .MuiTextField-root': {
			margin: '3px 0px',
		},
		'& .MuiInputBase-root': {
			padding: '3px',
		},
		'& .MuiInputBase-input': {
			fontSize: '12px',
			lineHeight: '16px',
		},
	},
	requirements: {
		maxHeight: '300px',
		overflowY: 'auto',
	},
	table: {
		marginTop: '10px',
		'& .MuiTableCell-head': {
			fontSize: '13px',
			fontWeight: 600,
			lineHeight: '18px',
			color: color.neutral[8],
			backgroundColor: color.neutral[2],
		},
		'& .MuiTableCell-body': {
			...themes.bodySmall,
			padding: '3px 8px !important',
		},
		'& .MuiTableCell-root': {
			padding: '5px 8px',
			border: 0,
		},
	},
}));
